/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { FunctionComponent } from 'preact';
import { HTMLAttributes } from 'preact/compat';
import { classes } from '@adobe-commerce/elsie/lib';
import { useText } from '@adobe-commerce/elsie/i18n';
import { InLineAlert, Skeleton, SkeletonRow } from '@adobe-commerce/elsie/components';
import { ShippingAddress } from '@/quote-management/data/models/negotiable-quote-model';
import '@/quote-management/components/ShippingAddressDisplay/ShippingAddressDisplay.css';
import { WarningFilled } from '@adobe-commerce/elsie/icons';

export interface ShippingAddressDisplayProps extends Omit<HTMLAttributes<HTMLDivElement>, 'loading'> {
  shippingAddress?: ShippingAddress;
  loading?: boolean;
  noAddressMessage?: string;
}

export const ShippingAddressDisplay: FunctionComponent<ShippingAddressDisplayProps> = ({
  className,
  shippingAddress,
  loading,
  noAddressMessage,
  ...props
}) => {
  const dictionary = useText({
    noAddressHeading: 'NegotiableQuote.Manage.shippingAddress.noAddressHeading',
    noAddressDescription: 'NegotiableQuote.Manage.shippingAddress.noAddressDescription',
  });

  if (loading) {
    return <ShippingAddressDisplaySkeleton />;
  }

  if (!shippingAddress) {
    return (
      <div 
        className={classes(['quote-management-shipping-address-display', 'quote-management-shipping-address-display--empty', className])}
        data-testid="shipping-address-display-empty"
        {...props}
      >
        <InLineAlert
          type="warning"
          variant="primary"
          data-testid="shipping-address-display-empty-alert"
          icon={<WarningFilled />}
          heading={dictionary.noAddressHeading}
          description={noAddressMessage || dictionary.noAddressDescription}
        />
      </div>
    );
  }

  return (
    <div 
      className={classes(['quote-management-shipping-address-display', className])}
      data-testid="shipping-address-display"
      {...props}
    >
      <div className="quote-management-shipping-address-display__field" data-testid="address-name">
        <span className="quote-management-shipping-address-display__name">
          {shippingAddress.firstname} {shippingAddress.lastname}
        </span>
      </div>
      
      {shippingAddress.company && (
        <div className="quote-management-shipping-address-display__field" data-testid="address-company">
          {shippingAddress.company}
        </div>
      )}
      
      {shippingAddress.street.map((line, index) => (
        <div 
          key={index} 
          className="quote-management-shipping-address-display__field" 
          data-testid={`address-street-${index}`}
        >
          {line}
        </div>
      ))}
      
      <div className="quote-management-shipping-address-display__field" data-testid="address-city-region">
        {shippingAddress.city}
        {/* The test is on the label rather than on the region object: a company
            address carries a region with no label, and the object alone being
            truthy printed the word "null" into the address. */}
        {shippingAddress.region?.label && `, ${shippingAddress.region.label}`}
        {' '}
        {shippingAddress.postcode}
      </div>
      
      <div className="quote-management-shipping-address-display__field" data-testid="address-country">
        {shippingAddress.country.label}
      </div>
      
      <div className="quote-management-shipping-address-display__field" data-testid="address-telephone">
        {shippingAddress.telephone}
      </div>
    </div>
  );
};

export const ShippingAddressDisplaySkeleton: FunctionComponent = () => {
  return (
    <Skeleton data-testid="shipping-address-display-skeleton">
      <SkeletonRow variant="row" fullWidth={true} size="small" lines={5} multilineGap='xsmall' />
    </Skeleton>
  );
};

